import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, inject } from '@angular/core';

import { PerformanceService } from '@frontend/sports/common/core/utils/performance';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { IPickTracking, SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { Observable, Subject, Subscription, exhaustMap, map } from 'rxjs';

import { PickId, V1PickId, V2OptionMarketPickId } from '../../betslip/core/picks/pick-id';
import { PriceType } from '../../betslip/core/picks/pick-models';
import { BetslipIntegrationService } from '../../betslip/integration/betslip-integration.service';
import {
    BETSLIP_LINE_SWITCHER_INJECTION_TOKEN,
    ILineSwitcherPickHandlerService,
} from '../../betslip/integration/services/line-switcher-pick-handler.service';
import { GeolocationCheckService } from '../geo-location/geo-location-check.service';

interface IPickState {
    selected: boolean;
    locked: boolean;
}

@Component({
    template: '',
})
export abstract class PickComponent implements OnInit, OnDestroy {
    private geolocationCheckService = inject(GeolocationCheckService);
    protected betslipIntegration = inject(BetslipIntegrationService);
    protected changeDetection = inject(ChangeDetectorRef);
    protected lineSwitcherPickHandlerService = inject<ILineSwitcherPickHandlerService | null>(BETSLIP_LINE_SWITCHER_INJECTION_TOKEN, {
        optional: true,
    });

    private clickSubject = new Subject<void>();
    protected pickSelectedSubscription: Subscription | null = null;

    @Input() trackingSource: string;
    @Input() trackingPosition?: number;
    @Input() enableOptionIndicator = true;
    @Input() sourceOfPick: SourceOfPick = SourceOfPick.Other;
    @Input() betDetails?: string;
    @Input() trackingGameDetails?: string;
    @Input() trackingModuleType?: string;
    @Input() isFallbackMarketEnabled?: boolean;
    @Input() isAutomatedMarquee?: boolean;
    @Input() marqueeName?: string;
    @Input() sitecoreTemplateId?: string;
    @Input() marqueeType?: string;
    @Input() disableOdds = false;
    @Input() tracking?: IPickTracking;

    state: IPickState = {
        selected: false,
        locked: false,
    };

    pickId: PickId;
    lineSwitcherCurrentSelection$: Observable<boolean> | undefined;

    private lowTierDevice = inject(PerformanceService).isLowTierDevice();
    private strategy = inject(RxStrategyProvider);
    private ngZone = inject(NgZone);

    ngOnInit(): void {
        this.clickSubject
            .pipe(
                exhaustMap(() => {
                    return this.togglePick();
                }),
            )
            .subscribe();
        if (this.lineSwitcherPickHandlerService) {
            this.lineSwitcherCurrentSelection$ = this.lineSwitcherPickHandlerService.currentPick$.pipe(
                map((p) => {
                    return p.toString() === this.pickId.toString();
                }),
            );
        }
    }

    ngOnDestroy(): void {
        this.clickSubject.complete();
        this.pickSelectedSubscription?.unsubscribe();
    }

    protected setPickId(pickId: PickId, priceType?: PriceType, longId?: string, siblingPickId?: PickId): void {
        if (!this.pickId || !this.pickId.isEqual(pickId)) {
            this.pickId = pickId;
            if (this.pickSelectedSubscription) {
                this.pickSelectedSubscription.unsubscribe();
            }

            const pickSelected$ =
                this.lineSwitcherPickHandlerService?.pickSelected$(this.pickId as V1PickId | V2OptionMarketPickId) ??
                this.betslipIntegration.pickSelected$(pickId, priceType, longId, siblingPickId);

            this.pickSelectedSubscription = pickSelected$.subscribe((selected) => {
                const isChanged = this.state.selected !== selected;
                if (isChanged) {
                    this.state.selected = selected;
                    this.changeDetection.markForCheck();
                }
            });
        }
    }

    pickClick(): void {
        if (this.lowTierDevice) {
            this.strategy
                .schedule(() => this._pickClick(), {
                    scope: this,
                    strategy: 'immediate',
                    patchZone: this.ngZone,
                })
                .subscribe();
        } else {
            this._pickClick();
        }
    }

    private _pickClick() {
        if (!this.disableOdds && !this.state.locked) {
            this.clickSubject.next();
        }
    }

    //TODO try to replace native price with Odds
    protected abstract get price(): NativePrice;

    protected abstract get priceId(): number | undefined;

    protected abstract addPick(): void;

    protected abstract removePick(): void;

    private togglePick(): Observable<void> {
        return new Observable<void>((subscriber) => {
            this.geolocationCheckService.checkGeolocation(() => {
                if (this.lineSwitcherPickHandlerService) {
                    !this.state.selected && this.lineSwitcherPickHandlerService.swap(this.pickId as V1PickId | V2OptionMarketPickId);
                } else {
                    if (this.state.selected) {
                        this.removePick();
                    } else {
                        this.addPick();
                    }
                }
                subscriber.complete();
            });
        });
    }
}
